import { useCallback, useEffect, useMemo } from 'react';
import { DropdownItem } from 'reactstrap';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { isEmpty } from 'lodash-es';
import { httpClient } from '@s20/services/httpClient';
import { getTimeZoneAbbr } from '@s20/common/util/date';
import { ApiRoutes, ChargeTypeResourceTypes, MomentDateFormat } from '../Common';
import { Modal } from '../common/Modal';
import { requiredStyle, ResourcePanel } from '../projectManager/ResourcePanel';
import { YesNoButtons } from '../common/forms/YesNoButtons';
import { TipAlert } from '../common/TipAlert';
import CustomCircularProgress from '../common/CustomCircularProgress';
import { Input, toasty } from '../common/forms/FormElements';
import { ValidationError } from '../common/forms/ValidationError';

// Indexed by moment's .day() (Sunday = 0), the same numbering as the server's DayOfWeek.
const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

// Day names in the order a job's week runs: starting on the weekday of the job's own week anchor (weekOfDate).
const getDayNamesFromAnchor = (weekOfDate) => {
    const anchorDay = moment(weekOfDate).day();

    return Array.from({ length: dayNames.length }, (_, offset) => dayNames[(anchorDay + offset) % dayNames.length]);
};

const emptyDayColumns = {
    sunday: 0,
    monday: 0,
    tuesday: 0,
    wednesday: 0,
    thursday: 0,
    friday: 0,
    saturday: 0,
};

const clearedDayLockColumns = dayNames.reduce(
    (acc, d) => ({ ...acc, [`${d}HasTimesheet`]: false, [`${d}HasAssignment`]: false }),
    {},
);

const jobResourcesQuery = (jobId) => ({
    queryKey: ['jobShiftResources', jobId],
    queryFn: () => httpClient.js(ApiRoutes.job.byId(jobId)).then((response) => response?.job ?? response),
});

// Days a resource is actually requested on, excluding shift rows and planning-only rows —
// mirrors the server's rule for which days have an original (parent) timesheet.
const getRequestedDayNames = (resources, weekOfDate) => getDayNamesFromAnchor(weekOfDate).filter(
    (d) => (resources ?? []).some((r) => !r.newShiftDate && !r.isPlanningOnly && r[d] > 0),
);

const getSelectableResources = (job) => (job?.resources ?? []).filter(
    (r) => !!r.id
        && !r.isPlanningOnly
        && (r.jobResourceTypeId === ChargeTypeResourceTypes.Employee || r.jobResourceTypeId === ChargeTypeResourceTypes.Equipment)
        // Only resources actually requested on this job — skip contract-catalog rows sitting at zero.
        && dayNames.some((d) => r[d] > 0),
);

const buildDraftResources = (job) => {
    const seenTypes = new Set();

    return getSelectableResources(job)
        .filter((r) => {
            const typeKey = [
                r.jobResourceTypeId,
                r.equipmentTypeId ?? '',
                r.applicationGroupId ?? '',
                r.applicationUserGroupId ?? '',
                r.certificationId ?? '',
            ].join('|');

            if (seenTypes.has(typeKey)) {
                return false;
            }
            seenTypes.add(typeKey);
            return true;
        })
        .map((r) => ({
            ...r,
            ...emptyDayColumns,
            ...clearedDayLockColumns,
            chargeTypeOptions: null,
            isNew: true,
            resourceName: r.resourceName || r.applicationGroupName || r.applicationUserGroupName || r.equipmentTypeName,
        }));
};

const getDayNamesFromDates = (dates, requestedDays) => (dates ?? [])
    .map((d) => dayNames[moment(d).day()])
    .filter((d) => requestedDays.includes(d));

const getRequestedResources = ({ dayName, shiftResources }) => (shiftResources ?? []).filter((r) => (parseInt(r[dayName], 10) || 0) > 0);

const AddReliefResourcesModalBody = ({
    jobId, selectedDate, values, setFieldValue,
    isShiftOverEstimate, timeZoneId, errors,
}) => {
    const jobQuery = useQuery(jobResourcesQuery(jobId));
    const job = jobQuery.data;

    const { dayName: selectedDayName } = values;

    // Once the job loads, preselect the board's selected day (or the first day the job runs)
    // and seed the draft grid. Only when not set yet — a cache update mid-edit must not
    // stomp the user's choices.
    useEffect(() => {
        if (!jobQuery.isSuccess) {
            return;
        }

        if (!selectedDayName) {
            const requestedDays = getRequestedDayNames(job?.resources, job?.weekOfDate);
            const daysFromBoard = getDayNamesFromDates([selectedDate], requestedDays);
            setFieldValue('dayName', daysFromBoard[0] ?? requestedDays[0] ?? null);
        }

        if (!values?.shiftResources) {
            setFieldValue('shiftResources', buildDraftResources(job));
        }
    }, [jobQuery.isSuccess, job, selectedDate, setFieldValue, selectedDayName, values?.shiftResources, values?.shiftDate]);

    const tzAbbreviation = useMemo(() => getTimeZoneAbbr(selectedDate, timeZoneId, values.shiftDate, values.shiftDate), [selectedDate, values.shiftDate, timeZoneId]);

    if (jobQuery.isLoading || !values?.shiftResources) {
        return (
            <div className="d-flex justify-content-center py-4">
                <CustomCircularProgress />
            </div>
        );
    }

    if (jobQuery.isError) {
        return <div className="text-danger">Failed to load job resources.</div>;
    }

    const hasError = !isEmpty(errors);

    return (
        <>
            <ResourcePanel
                job={{ ...job, resources: values.shiftResources }}
                selectedDate={moment(selectedDate).toDate()}
                edit={{ allowResources: true, resourceErrors: {} }}
                errors={errors}
                tableRequired={hasError ? requiredStyle : undefined}
                onJobChange={(updatedJob) => setFieldValue('shiftResources', updatedJob.resources)}
                excludeResourceKits
            />

            <YesNoButtons
                id="isNewShift"
                name="isNewShift"
                label="This is a new shift"
                required
                disabled={!isShiftOverEstimate}
                TipComponentProps={isShiftOverEstimate ? null : {
                    label: 'A new shift can only be created if the job is over estimate or a timesheet has been submitted.',
                }}
            />

            {!!values?.isNewShift && (
                <>
                    <TipAlert
                        containerClassName="mt-2"
                        label="A linked timesheet will be created for the new shift; its charges are entered against the shift start date."
                    />

                    <Input
                        id="shiftDate"
                        name="shiftDate"
                        label="Shift Start"
                        required
                        type="datetime-local"
                        min={moment(selectedDate).startOf('day').format(MomentDateFormat.DateTimeISO8601Short)}
                        error="A shift start date and time are required."
                        cleanText={false}
                        endAddonClassName="inset"
                        endAddon={(
                            <span className="text-muted">
                                {tzAbbreviation}
                            </span>
                        )}
                    />
                </>
            )}
        </>
    );
};

const useAddReliefResourcesModal = ({
    timeZoneId, jobId, selectedDate, isShiftOverEstimate,
}) => {
    const queryClient = useQueryClient();

    const saveResources = useMutation({
        mutationFn: async (values) => {
            const { dayName } = values;
            const requested = getRequestedResources(values);

            // The save button is live while the body is still loading; nothing is
            // editable until the job query has resolved.
            if (!dayName || !requested.length) {
                throw new ValidationError({ resources: 'Enter a quantity for at least one resource.' });
            }

            if (values.isNewShift && !values.shiftDate) {
                throw new ValidationError({ shiftDate: 'A shift start date and time are required.' });
            }

            const { queryKey } = jobResourcesQuery(jobId);
            const job = queryClient.getQueryData(queryKey) ?? await queryClient.fetchQuery(jobResourcesQuery(jobId));
            const selectable = getSelectableResources(job);

            const newRows = requested.map((draft) => {
                const source = selectable.find((r) => r.id === draft.id);

                if (!source) {
                    throw new Error(`"${draft.resourceName}" is no longer on this job.`);
                }

                return {
                    ...source,
                    ...emptyDayColumns,
                    id: 0,
                    [dayName]: parseInt(draft[dayName], 10),
                    chargeTypeId: draft.chargeTypeId,
                    newShiftDate: values.isNewShift ? moment(values.shiftDate).format() : null,
                };
            });

            return httpClient.put(ApiRoutes.job.updateResources(job.id), { resources: newRows });
        },
        onSuccess: (data, values) => {
            toasty.success(
                values.isNewShift
                    ? 'Resources added. A linked timesheet was created for the new shift.'
                    : 'Resources added.',
            );
        },
    });

    return useCallback(() => {
        Modal.show({
            title: 'Add Relief Resources',
            onSubmit: saveResources.mutateAsync,
            initialValues: { isNewShift: !!isShiftOverEstimate, shiftDate: moment(selectedDate).utcOffset(selectedDate).format(MomentDateFormat.DateTimeISO8601Short) },
            SaveButtonProps: { savingLabel: 'Saving...' },
            body: (bodyProps) => (
                <AddReliefResourcesModalBody
                    {...bodyProps}
                    jobId={jobId}
                    selectedDate={selectedDate}
                    isShiftOverEstimate={!!isShiftOverEstimate}
                    timeZoneId={timeZoneId}
                />
            ),
        });
    }, [jobId, selectedDate, isShiftOverEstimate, timeZoneId, saveResources.mutateAsync]);
};

export const AddReliefResources = ({
    timeZoneId, jobId, selectedDate, isShiftOverEstimate,
}) => {
    const showModal = useAddReliefResourcesModal({
        timeZoneId, jobId, selectedDate, isShiftOverEstimate,
    });

    return (
        <DropdownItem onClick={showModal}>
            Add Relief Resources
        </DropdownItem>
    );
};

export default AddReliefResources;
